import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import React from 'react'
import Stack from '@mui/material/Stack'
import Avatar from '@mui/material/Avatar'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Fade from '@mui/material/Fade'
import AvatarGroup from '@mui/material/AvatarGroup'
import IconButton from '@mui/material/IconButton'
import Box from '@mui/material/Box'

import {
  useCommunicationAction,
  useCommunicationSelect,
} from 'src/hooks/useCommunication'
import { useAccountSelect } from 'src/hooks/useAccount'
import { useRouterObjectPage } from 'src/hooks/router/useRouterObjectPage'
import { useSearchQuery } from 'src/hooks/useSearchQuery'
import { ICall } from 'src/store/modules/communication/types'
import UserIcon from 'src/layouts/components/UserIcon'
import { getAvatarSx } from 'src/components/common/personaAvatar'
import ActiveCallsPopover, { getActiveCallLabel } from './ActiveCallsPopover'
// import { MOCK_ACTIVE_CALLS } from './helpers'
import { ActiveCall } from 'src/store/modules/communication/state'

export const RingCentralIncomingCallUi = () => {
  const { t } = useTranslation('common')
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null)
  const navigate = useNavigate()
  const activeCalls = useCommunicationSelect.activeCalls()
  // const activeCalls = MOCK_ACTIVE_CALLS

  const user = useAccountSelect.user()
  const getActiveCalls = useCommunicationAction.getActiveCalls()

  React.useEffect(() => {
    if (!user?.id) return

    getActiveCalls({
      assignedUserId: user.id as number,
      accountDriver: 'ring-central',
    })
  }, [user?.id])

  const handleOpenActiveCalls = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
    setAnchorEl(e.currentTarget)
  }

  const handleOpenCallHistory = () => {
    setAnchorEl(null)
    navigate('/callHistory')
  }

  const routeObjectPage = useRouterObjectPage()
  const { setPathQuery } = useSearchQuery()

  const handleOpenCallDetails = (call: ActiveCall) => {
    if (!call?.subscriber?.storageObjects?.[0]) return

    setAnchorEl(null)
    const {
      uniqId,
      objectSchemeIdentifier,
      parentObjectUniqId,
      parentObjectSchemeIdentifier,
    } = call?.subscriber.storageObjects[0]

    if (parentObjectSchemeIdentifier && parentObjectUniqId) {
      routeObjectPage.selectObjectChildrenPath(
        `/storage/${parentObjectSchemeIdentifier}/${parentObjectUniqId}`,
        {
          schemeName: objectSchemeIdentifier,
          objectUniqId: uniqId,
        }
      )
    } else if (objectSchemeIdentifier && uniqId) {
      setPathQuery(`/storage/${objectSchemeIdentifier}/${uniqId}`, {})
    }
  }

  React.useEffect(() => {
    if (activeCalls.length === 0) setAnchorEl(null)
  }, [activeCalls.length])

  return (
    <>
      <Fade
        in={activeCalls.length > 0}
        timeout={300}
        mountOnEnter
        unmountOnExit
      >
        <Stack
          direction="row"
          alignItems="center"
          columnGap={2}
          onClick={handleOpenActiveCalls}
          sx={{ cursor: 'pointer' }}
        >
          <AvatarGroup
            max={4}
            sx={{
              '& .MuiAvatarGroup-avatar:first-of-type': {
                fontSize: 12,
                width: 28,
                height: 28,
              },
            }}
          >
            {activeCalls.map((call) => {
              const label = getActiveCallLabel(
                call,
                t('currentCalls.unknown', 'Unknown')
              )

              return (
                <Avatar
                  key={call.id}
                  alt={label.name}
                  src={label.icon}
                  sx={getAvatarSx(
                    28,
                    Boolean(label.icon),
                    Math.round(28 * 0.4)
                  )}
                >
                  {label.initial}
                </Avatar>
              )
            })}
          </AvatarGroup>
          <Stack direction="column">
            <Typography fontSize={14} fontWeight={600} color="grey.900">
              {t('currentCalls.countLabel', {
                count: activeCalls.length,
                defaultValue: '{{count}} active',
              })}
            </Typography>
            <Stack direction="row" alignItems="center" columnGap={1}>
              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  flexShrink: 0,
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: 'success.main',
                  boxShadow: '0 0 0 0 rgba(11, 214, 141, 0.55)',
                  animation: 'activeCallLive 1.6s ease-out infinite',
                  '@keyframes activeCallLive': {
                    '0%': { boxShadow: '0 0 0 0 rgba(11, 214, 141, 0.55)' },
                    '100%': { boxShadow: '0 0 0 8px rgba(11, 214, 141, 0)' },
                  },
                }}
              />
              <Typography fontSize={11} color="grey.500">
                {t('currentCalls.callsLabel', {
                  count: activeCalls.length,
                  defaultValue: 'calls',
                })}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </Fade>
      <ActiveCallsPopover
        handleOpenCallDetails={handleOpenCallDetails}
        anchorEl={anchorEl}
        calls={activeCalls}
        onClose={() => setAnchorEl(null)}
        onOpenCallHistory={handleOpenCallHistory}
      />
    </>
  )
}

interface IIncomingCallCardProps {
  activeCall: ICall
}
export const IncomingCallCard = (props: IIncomingCallCardProps) => {
  const { activeCall } = props
  const { setPathQuery } = useSearchQuery()
  const routeObjectPage = useRouterObjectPage()
  const setActiveCallsClose = useCommunicationAction.setActiveCallsClose()

  const handleOpenDetails = () => {
    if (!activeCall?.subscriber?.storageObjects?.[0]) return
    const {
      uniqId,
      objectSchemeIdentifier,
      parentObjectUniqId,
      parentObjectSchemeIdentifier,
    } = activeCall?.subscriber.storageObjects[0]

    if (parentObjectSchemeIdentifier && parentObjectUniqId) {
      routeObjectPage.selectObjectChildrenPath(
        `/storage/${parentObjectSchemeIdentifier}/${parentObjectUniqId}`,
        {
          schemeName: objectSchemeIdentifier,
          objectUniqId: uniqId,
        }
      )
    } else if (objectSchemeIdentifier && uniqId) {
      setPathQuery(`/storage/${objectSchemeIdentifier}/${uniqId}`, {})
    }
  }

  return (
    <Stack
      direction="row"
      spacing={3}
      alignItems="center"
      justifyContent="space-between"
      px={4}
      py={5}
      pr={12}
      width={370}
      sx={{
        position: 'relative',
        borderRadius: '12px',
        background: '#030426',
        boxShadow: '0px 4px 24px 0px rgba(0, 0, 0, 0.12)',
      }}
    >
      <Stack direction="row" spacing={3} alignItems="center">
        <Avatar
          src={activeCall?.subscriber?.storageObjects?.[0]?.icon}
        ></Avatar>
        <Stack spacing={1}>
          <Stack>
            <Typography
              variant="subtitle2"
              fontWeight={'bolder'}
              color={'white'}
            >
              {activeCall?.subscriber?.storageObjects?.[0]?.primary ||
                'Unknown'}
            </Typography>
            {/* <Typography>Samsung</Typography> */}
          </Stack>
          <Stack>
            <Typography variant="caption" color={'grey.500'}>
              {activeCall?.subscriber?.identifier || 'Unknown'}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
      <Stack>
        {activeCall?.subscriber?.storageObjects?.[0] && (
          <Button
            variant="outlined"
            size="small"
            sx={{ px: 2, py: 2, fontWeight: 400 }}
            onClick={handleOpenDetails}
          >
            Open details
          </Button>
        )}
        <IconButton
          size="small"
          color="error"
          onClick={() => setActiveCallsClose({ id: activeCall.id })}
          sx={{
            position: 'absolute',
            right: 0,
            top: 0,
          }}
        >
          <UserIcon
            icon="material-symbols-light:cancel-outline"
            fontSize={22}
          />
        </IconButton>
      </Stack>
    </Stack>
  )
}
